import { QueryClientProvider } from '@tanstack/react-query';
import { App as AntdAppContext, ConfigProvider } from 'antd';
import { Suspense, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { RouterProvider } from 'react-router-dom';
import { PageLoader } from './components/page-loader';
import { TasksWatcher } from './components/tasks-watcher';
import { queryClient } from './config/react-query';
import { router } from './routing/routes';
import { antdTheme } from './theme';

const App = () => {
  const auth = useAuth();

  // Token refresh listener - automatically refresh token before expiration
  useEffect(() => {
    return auth.events.addAccessTokenExpiring(() => {
      auth.signinSilent();
    });
  }, [auth.events, auth]);

  // Handle navigation states
  switch (auth.activeNavigator) {
    case 'signinSilent':
      return <PageLoader />;
    case 'signoutRedirect':
      return <PageLoader />;
  }

  // Show loader while checking auth state
  if (auth.isLoading) {
    return <PageLoader />;
  }

  // Redirect to signin if not authenticated
  if (!auth.isAuthenticated) {
    auth.signinRedirect();
    return <PageLoader />;
  }

  // Handle auth errors
  if (auth.error) {
    return (
      <ConfigProvider theme={antdTheme}>
        <PageLoader />
      </ConfigProvider>
    );
  }

  // Render app if authenticated
  return (
    <ConfigProvider theme={antdTheme}>
      <AntdAppContext>
        <QueryClientProvider client={queryClient()}>
          <Suspense fallback={<PageLoader />}>
            <TasksWatcher />
            <RouterProvider router={router} />
          </Suspense>
        </QueryClientProvider>
      </AntdAppContext>
    </ConfigProvider>
  );
};

export default App;
